import { customAlphabet } from 'nanoid';
import * as topicUI from './topicUI/index.js';
import * as channelUI from './channelUI/index.js';
import lang from '../../../lang/prop-en-module.js';
import { getSMSFrom, setSMSFrom } from './settings.js';

const topicLang = lang.notification.topic;
const channelLang = lang.notification.channel;
const getId = customAlphabet('0123456789abcdefghijklmnopqrstuvwxyz', 10);

let isAlreadyInit = false;

export function initNotificationSettings(
  notificationData = { cachedTopicsData: {}, channels: [], topics: [], smsSettings: { from: '' } }
) {
  // NOTE: Format of data
  /*
  const channels = [
    {
      id: getId(),
      name: 'Email 1',
      type: 'email',
      subject: 'Subject 1',
      body: 'Body 1',
    },
    {
      id: getId(),
      name: 'SMS 1',
      type: 'sms',
      message: 'Message 1',
    },
  ];
  const notificationData = {
    cachedTopicsData: {},
    channels,
    topics: [
      {
        id: getId(),
        key: 'topic_1',
        emailChannel: channels[0],
        smsChannel: channels[1],
      },
    ],
    smsSettings: {
      from: 'SMS From',
    },
  };
  */

  // NOTE: This ensures that when cancelling an update, we render the data from the store, not the entered data
  channelUI.table.set(notificationData.channels);
  topicUI.table.set(notificationData.topics);
  setSMSFrom(notificationData.smsSettings.from);

  if (isAlreadyInit) return;
  initTopicsSettings();
  initChannelsSettings();
  isAlreadyInit = true;
}

function replaceSpacesWithUnderscores(input) {
  return input.replace(/\s/g, '_');
}

export function getNotificationData() {
  return {
    cachedTopicsData: getCachedTopicsData(),
    topics: topicUI.table.getData(),
    channels: channelUI.table.getData(),
    smsSettings: {
      from: getSMSFrom(),
    },
  };
}

function getCachedTopicsData() {
  const topics = topicUI.table.getData();
  return topics.reduce((acc, topic) => {
    acc[topic.key] = {
      emailSubject: topic.emailChannel.subject || '',
      emailBody: topic.emailChannel.body || '',
      SMSMessage: topic.smsChannel.message || '',
    };
    return acc;
  }, {});
}

function onValid(validationResults, cb) {
  const validate = validationResults.find((validation) => !validation.isValid);
  if (!validate) {
    cb();
  } else {
    alert(validate.message);
  }
}

function initTopicsSettings() {
  const { view, table, form } = topicUI;

  view.showContent();

  view.onTriggerNew(() => {
    form.fill(
      {
        mode: 'create',
        title: topicLang.form.titleCreate,
        saveBtnText: topicLang.form.btnCreate,
      },
      { id: getId(), emailChannel: {}, smsChannel: {} },
      channelUI.table.getData()
    );
    view.showForm();
  });

  table.onTriggerEdit((id) => {
    const foundTopic = table.getData().find((topic) => topic.id === id);

    form.fill(
      {
        mode: 'edit',
        title: topicLang.form.titleEdit,
        saveBtnText: topicLang.form.btnUpdate,
      },
      foundTopic,
      channelUI.table.getData()
    );
    view.showForm();
  });

  table.onTriggerRemove(table.remove.bind(table));

  form.onChangeKey(replaceSpacesWithUnderscores);

  form.onCancel(view.showContent.bind(view));

  form.onSubmit(({ mode, data }) => {
    onValid([isValidTopicKey(data, topicUI.table.getData())], () => {
      if (mode === 'create') {
        table.add(data);
      } else {
        table.update(data);
      }
      view.showContent();
    });
  });

  onHideModal(() => {
    view.showContent();
  });
}

function isValidTopicKey(formData, topics) {
  if (formData.key === '') return { isValid: false, message: topicLang.form.validation.requiredKey };
  if (topics.find((topic) => topic.id !== formData.id && topic.key === formData.key)) {
    return { isValid: false, message: topicLang.form.validation.uniqueKey };
  }
  return { isValid: true, message: '' };
}

function initChannelsSettings() {
  const { view, table, form } = channelUI;
  view.showContent();

  view.onTriggerNew(() => {
    form.fill(
      {
        mode: 'create',
        title: channelLang.form.titleCreate,
        saveBtnText: channelLang.form.btnCreate,
      },
      { id: getId(), type: 'email' }
    );
    view.showForm();
  });

  table.onTriggerEdit((id) => {
    const foundChannel = table.getData().find((channel) => channel.id === id);

    form.fill(
      {
        mode: 'edit',
        title: channelLang.form.titleEdit,
        saveBtnText: channelLang.form.btnUpdate,
      },
      foundChannel
    );
    view.showForm();
  });

  table.onTriggerRemove((id) => {
    topicUI.table.map((topic) => {
      if (topic.emailChannel.id === id) {
        return { ...topic, emailChannel: {} };
      }
      if (topic.smsChannel.id === id) {
        return { ...topic, smsChannel: {} };
      }
      return topic;
    });
    table.remove(id);
  });

  form.onCancel(view.showContent.bind(view));

  form.onSubmit(({ mode, data }) => {
    onValid(
      [
        isValidChannelName(data, channelUI.table.getData()),
        isValidEmailSubject(data),
        isValidEmailBody(data),
        isValidSMSMessage(data),
      ],
      () => {
        if (mode === 'create') {
          table.add(data);
        } else {
          table.update(data);

          topicUI.table.map((topic) => {
            if (topic.emailChannel.id === data.id) {
              return { ...topic, emailChannel: data };
            }
            if (topic.smsChannel.id === data.id) {
              return { ...topic, smsChannel: data };
            }
            return topic;
          });
        }
        view.showContent();
      }
    );
  });

  onHideModal(() => {
    view.showContent();
  });
}

function isValidChannelName(formData, channels) {
  if (formData.name === '') {
    return { isValid: false, message: channelLang.form.validation.requiredName };
  }
  if (channels.find((channel) => channel.id !== formData.id && channel.name === formData.name)) {
    return { isValid: false, message: channelLang.form.validation.uniqueName };
  }
  return { isValid: true, message: '' };
}

function isValidEmailSubject(formData) {
  if ('subject' in formData && formData.subject === '') {
    return { isValid: false, message: channelLang.form.validation.requiredEmailSubject };
  }
  return { isValid: true, message: '' };
}

function isValidEmailBody(formData) {
  if ('body' in formData && formData.body === '') {
    return { isValid: false, message: channelLang.form.validation.requiredEmailBody };
  }
  return { isValid: true, message: '' };
}

function isValidSMSMessage(formData) {
  if ('message' in formData && formData.message === '') {
    return { isValid: false, message: channelLang.form.validation.requiredSMSMessage };
  }
  return { isValid: true, message: '' };
}

function onHideModal(cb) {
  $('#eventEditModal').on('hidden.bs.modal', cb);
}
